import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { FileText, Check, X, Download, Calendar, User, Clock, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './config';

const API = API_URL;

// Estado badges con colores
const ESTADO_CONFIG = {
  borrador: { label: 'Borrador', color: 'bg-slate-600', icon: '📝' },
  enviado: { label: 'Enviado', color: 'bg-blue-600', icon: '📤' },
  visto: { label: 'En revisión', color: 'bg-purple-600', icon: '👁️' },
  aceptado: { label: 'Aceptado', color: 'bg-emerald-600', icon: '✅' },
  rechazado: { label: 'Rechazado', color: 'bg-red-600', icon: '❌' },
  pendiente: { label: 'Pendiente', color: 'bg-amber-600', icon: '⏳' },
};

export const PublicPresupuestoPage = () => {
  // Get presupuesto ID from URL
  const presupuestoId = window.location.pathname.split('/presupuesto/')[1];
  
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [firmante, setFirmante] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Fetch presupuesto
  useEffect(() => {
    const fetchPresupuesto = async () => {
      if (!presupuestoId) {
        setError('ID de presupuesto no válido');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/presupuestos/portal/${presupuestoId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Presupuesto no encontrado');
          } else {
            setError('Error al cargar el presupuesto');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setPresupuesto(data);
      } catch (err) {
        console.error('Error:', err);
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    fetchPresupuesto();
  }, [presupuestoId]);

  // Handle accept
  const handleAccept = async () => {
    if (!firmante.trim()) {
      toast.error('Por favor, introduce tu nombre');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/presupuestos/portal/${presupuestoId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aceptar: true,
          nombre_firmante: firmante
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('¡Presupuesto aceptado! Nos pondremos en contacto contigo.');
        setPresupuesto({ ...presupuesto, estado: 'aceptado' });
        setShowAcceptDialog(false);
      } else {
        toast.info(data.message);
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al procesar la respuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/presupuestos/portal/${presupuestoId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aceptar: false,
          nombre_firmante: firmante || 'Cliente',
          motivo_rechazo: motivoRechazo
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.info('Presupuesto rechazado. Gracias por tu respuesta.');
        setPresupuesto({ ...presupuesto, estado: 'rechazado' });
        setShowRejectDialog(false);
      } else {
        toast.info(data.message);
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al procesar la respuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API}/presupuestos/public/${presupuestoId}/pdf`);
      
      if (!response.ok) throw new Error('Error al descargar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presupuesto_${presupuesto?.cliente_nombre?.replace(/\s+/g, '_') || 'documento'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF descargado');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error al descargar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando presupuesto...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Error</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const estadoConfig = ESTADO_CONFIG[presupuesto?.estado] || ESTADO_CONFIG.pendiente;
  const isRespondable = !['aceptado', 'rechazado'].includes(presupuesto?.estado);
  const fechaEvento = presupuesto?.fecha_evento 
    ? new Date(presupuesto.fecha_evento).toLocaleDateString('es-ES', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    : 'Por confirmar';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-900/30 to-emerald-900/30 border-b border-slate-700">
        <div className="container max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-cyan-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Presupuesto</h1>
          </div>
          <p className="text-slate-400">
            para {presupuesto?.cliente_nombre || 'Cliente'}
          </p>
        </div>
      </div>

      <div className="container max-w-3xl mx-auto px-4 py-8">
        {/* Status Banner */}
        {presupuesto?.estado === 'aceptado' && (
          <Card className="bg-emerald-900/30 border-emerald-600/50 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-emerald-400 font-semibold">¡Presupuesto Aceptado!</p>
                <p className="text-slate-400 text-sm">Nos pondremos en contacto contigo pronto.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {presupuesto?.estado === 'rechazado' && (
          <Card className="bg-red-900/30 border-red-600/50 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-red-400 font-semibold">Presupuesto Rechazado</p>
                <p className="text-slate-400 text-sm">Gracias por tu respuesta.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Event Info */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-900/50 rounded-lg">
                  <User className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Cliente</p>
                  <p className="text-white font-semibold">{presupuesto?.cliente_nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-900/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Fecha del Evento</p>
                  <p className="text-white font-semibold capitalize">{fechaEvento}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white text-lg">Servicios Incluidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {presupuesto?.servicios?.map((servicio, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{servicio.nombre}</p>
                    {servicio.descripcion && (
                      <p className="text-slate-400 text-sm">{servicio.descripcion}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-semibold">
                      {(servicio.precio * (servicio.cantidad || 1)).toFixed(2)}€
                    </p>
                    {servicio.cantidad > 1 && (
                      <p className="text-slate-500 text-xs">{servicio.cantidad} x {servicio.precio}€</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="p-6">
            <div className="space-y-3">
              <div className="flex justify-between text-slate-300">
                <span>Subtotal</span>
                <span>{presupuesto?.subtotal?.toFixed(2) || '0.00'}€</span>
              </div>
              
              {presupuesto?.descuento_porcentaje > 0 && (
                <div className="flex justify-between text-amber-400">
                  <span>Descuento ({presupuesto.descuento_porcentaje}%)</span>
                  <span>-{presupuesto?.total_descuento?.toFixed(2) || '0.00'}€</span>
                </div>
              )}
              
              <div className="flex justify-between text-slate-300 font-medium">
                <span>Base Imponible</span>
                <span>{presupuesto?.base_imponible?.toFixed(2) || '0.00'}€</span>
              </div>
              
              <div className="flex justify-between text-slate-300">
                <span>IVA ({presupuesto?.iva || 10}%)</span>
                <span>+{presupuesto?.total_iva?.toFixed(2) || '0.00'}€</span>
              </div>
              
              <Separator className="bg-slate-600" />
              
              <div className="flex justify-between text-2xl font-bold">
                <span className="text-white">TOTAL</span>
                <span className="text-emerald-400">{presupuesto?.total?.toFixed(2) || '0.00'}€</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        {presupuesto?.notas && (
          <Card className="bg-slate-800 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Notas y Condiciones</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-300 whitespace-pre-wrap">{presupuesto.notas}</p>
            </CardContent>
          </Card>
        )}

        {/* Validity */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <p className="text-slate-400">
              Este presupuesto tiene una validez de <span className="text-amber-400 font-semibold">{presupuesto?.validez_dias || 15} días</span>
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isRespondable && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => setShowAcceptDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700 h-14 text-lg"
              >
                <Check className="w-5 h-5 mr-2" />
                Aceptar
              </Button>
              <Button
                onClick={() => setShowRejectDialog(true)}
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-600/20 h-14 text-lg"
              >
                <X className="w-5 h-5 mr-2" />
                Rechazar
              </Button>
            </div>
          )}

          <Button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 h-12"
          >
            {downloadingPdf ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Download className="w-5 h-5 mr-2" />
            )}
            Descargar PDF
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          <p>¿Tienes alguna pregunta? Contáctanos</p>
        </div>
      </div>

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              Confirmar Aceptación
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Al aceptar este presupuesto, confirmas tu interés en contratar nuestros servicios.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Tu nombre *</label>
              <Input
                value={firmante}
                onChange={(e) => setFirmante(e.target.value)}
                placeholder="Nombre completo"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div className="bg-emerald-900/20 border border-emerald-600/30 rounded-lg p-4">
              <p className="text-emerald-400 font-semibold mb-1">Total a contratar</p>
              <p className="text-2xl font-bold text-white">{presupuesto?.total?.toFixed(2)}€</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAcceptDialog(false)}
              className="border-slate-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAccept}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Confirmar Aceptación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <XCircle className="w-6 h-6 text-red-400" />
              Rechazar Presupuesto
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Lamentamos que no se ajuste a tus necesidades. ¿Podrías indicarnos el motivo?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Tu nombre (opcional)</label>
              <Input
                value={firmante}
                onChange={(e) => setFirmante(e.target.value)}
                placeholder="Nombre"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Motivo del rechazo (opcional)</label>
              <Textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Ej: Precio elevado, fechas no disponibles, etc."
                className="bg-slate-700 border-slate-600 text-white"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              className="border-slate-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReject}
              disabled={submitting}
              variant="destructive"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Confirmar Rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicPresupuestoPage;
